import React, { useState } from 'react';
import { Brain, Calendar, AlertCircle, ChevronRight } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useReports } from '../hooks/useReports';
import { collectActionableTickers } from './reportHelpers';

/**
 * Daily report list page. Each row links to /analysis/:reportId for the
 * detail view. Consumes the V2 structuredOutput shape from
 * GET /api/reports (see backend Track F.6).
 */
const Analysis = () => {
  const navigate = useNavigate();
  const { reports, loading, error } = useReports(30);
  const [filter, setFilter] = useState('all'); // all, actionable, alerts

  /** Defensive accessors -- structuredOutput should be present on V2
   *  reports, but legacy migrated docs might lack a section. */
  const so = (r) => r.structuredOutput || {};
  const themes = (r) => so(r).themes || [];
  const callsToAction = (r) => so(r).callsToAction || [];
  const portfolioAlerts = (r) => so(r).portfolioAlerts || [];
  const summary = (r) => so(r).summary || '';

  const reportDate = (r) => {
    const raw = r.reportDate || r.generatedAt;
    if (!raw) return null;
    try {
      return typeof raw === 'string' ? parseISO(raw) : new Date(raw);
    } catch {
      return null;
    }
  };

  const filteredReports = reports.filter((r) => {
    if (filter === 'all') return true;
    if (filter === 'actionable') return callsToAction(r).length > 0;
    if (filter === 'alerts')     return portfolioAlerts(r).length > 0;
    return true;
  });

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error">
        <AlertCircle size={24} />
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>AI Investment Analysis</h1>
          <p>Claude-powered daily reports based on Russ's premium alerts</p>
        </div>
      </div>

      <div className="filter-buttons">
        <button
          className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setFilter('all')}
        >
          All ({reports.length})
        </button>
        <button
          className={`btn ${filter === 'actionable' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setFilter('actionable')}
        >
          Actionable ({reports.filter(r => callsToAction(r).length > 0).length})
        </button>
        <button
          className={`btn ${filter === 'alerts' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setFilter('alerts')}
        >
          With Alerts ({reports.filter(r => portfolioAlerts(r).length > 0).length})
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {filteredReports.map((report) => {
          const date = reportDate(report);
          const reportThemes = themes(report);
          const reportCalls = callsToAction(report);
          const reportAlerts = portfolioAlerts(report);
          // Show top 3 actionable tickers inline -- more useful than a count badge.
          const topTickers = collectActionableTickers(report).slice(0, 3);

          return (
            <button
              key={report.id}
              onClick={() => navigate(`/analysis/${encodeURIComponent(report.id)}`)}
              className="card analysis-list-card"
              style={{
                cursor: 'pointer',
                textAlign: 'left',
                width: '100%',
                padding: '1rem',
              }}
            >
              <div className="analysis-card-header">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                    <Brain size={20} style={{ color: 'var(--primary-color)', flexShrink: 0, marginTop: '2px' }} />
                    <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0, lineHeight: 1.4 }}>
                      {summary(report) || 'Daily Investment Report'}
                    </h3>
                  </div>

                  <div className="analysis-meta">
                    {date && (
                      <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                        <Calendar size={14} />
                        {format(date, 'MMM dd, yyyy')}
                      </span>
                    )}
                    {topTickers.map((t) => (
                      // Inner button so the ticker is clickable independently
                      // of the card-as-button wrapper. stopPropagation prevents
                      // both navigations from firing on the same click.
                      <span
                        key={t.ticker}
                        role="link"
                        tabIndex={0}
                        className={`badge ${t.cssBadge}`}
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/mentions/tickers/${encodeURIComponent(t.ticker)}`);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.stopPropagation();
                            e.preventDefault();
                            navigate(`/mentions/tickers/${encodeURIComponent(t.ticker)}`);
                          }
                        }}
                      >
                        {t.action} {t.ticker}
                      </span>
                    ))}
                    {reportCalls.length > topTickers.length && (
                      <span className="badge badge-info">
                        +{reportCalls.length - topTickers.length} more
                      </span>
                    )}
                    {topTickers.length === 0 && reportThemes.length > 0 && (
                      <span className="badge badge-info">
                        {reportThemes.length} theme{reportThemes.length === 1 ? '' : 's'}
                      </span>
                    )}
                    {reportAlerts.length > 0 && (
                      <span className="badge badge-warning">
                        {reportAlerts.length} alert{reportAlerts.length === 1 ? '' : 's'}
                      </span>
                    )}
                  </div>
                </div>

                <ChevronRight size={20} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
              </div>
            </button>
          );
        })}

        {filteredReports.length === 0 && (
          <div className="empty-state">
            <Brain size={48} />
            <p>No reports found{filter !== 'all' ? ' for selected filter' : ''}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Analysis;
